import { useEffect, useRef } from "react";
import { useLocation } from "@reach/router";
import PropTypes from "prop-types";
import { environmentMap, eb360MapURI } from "constants/applicationConstant"
import RemoteComponentLoader from "./remote-component";
import { ThemeProvider } from "@mui/material";
import ebTheme from "@eb/ui360";


/***
 * EB360MapComponent
 * @param {string} idToken - The id token for authentication
 * @param {string} lang - The language for the map
 * @param {object} coordinates - The coordinates for the map
 * @param {object} address - The address for the map
 * @returns {JSX.Element} - The EB360MapComponent
 * @description This component is used to load the EB360 map with the given coordinates and address.
 * It uses the RemoteComponentLoader to load the map from the given URL.
 */

const EB360MapComponent = (props) => {
  const { address, idToken, lang, coordinates, editAddress, onPointAddressChange, onPointChange } = props;
  const mapRef = useRef(null);
  const { hostname } = useLocation();
  const environment = environmentMap[hostname] || "production";
  const eb360MapURL = eb360MapURI[environment];

  useEffect(() => {
    window.EB_REMOTE_ENTRIES = { eb360MapControl: eb360MapURL }
  }, [])

  const handlePointAddressChange = (pointAddress) => {
    onPointAddressChange && onPointAddressChange(pointAddress);
  }
  const handlePointChange = (point) => {
    const [lng, lat] = point.coordinates
    onPointChange && onPointChange({lng, lat});
  }

  const theme = ebTheme("light");

  return (
    <ThemeProvider theme={theme}>
      <RemoteComponentLoader
        url={eb360MapURL}
        scope="eb360MapControl"
        module="./LocationEditControl"
        idToken={idToken}
        locale={lang}
        environment={environment}
        apiRef={mapRef}
        enableEditPoint={editAddress}
        onPointChange={handlePointChange}
        onPointAddressChange={handlePointAddressChange}
        contentStyle={{
          featureStyle: {
            icon: "Dot",
            iconColor: "rgba(234, 12, 12, 0.86)",
            iconFrame: "MapIconAsset",
            fill: "rgba(234, 12, 12, 0.86)",
            stroke: "rgba(234, 12, 12, 0.86)",
            strokeWidth: 2,
          }
        }}
        initialMapView={{
          "type": 'zoomLevel',
          "zoomLevel": 14,
          "centerLat": Number(coordinates.lat),
          "centerLon": Number(coordinates.lng)
        }}
        initialContent={{
          centerOnContent: true,
          point: {
            type: "Point",
            coordinates: [
              Number(coordinates.lng),
              Number(coordinates.lat),
            ]
          },
          pointAddress: {
            formattedAddress: address
          },
        }}
      />
    </ThemeProvider>
  )
}

EB360MapComponent.propTypes = {
  editAddress: PropTypes.bool,
  idToken: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired,
  handlePointChange: PropTypes.func,
  handlePointAddressChange: PropTypes.func,
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired
  }).isRequired,
  address: PropTypes.shape({
    formattedAddress: PropTypes.string
  })
};

EB360MapComponent.defaultProps = {
  address: {
    formattedAddress: ""
  },
  coordinates: {
    lat: 0,
    lng: 0
  },
  editAddress: false,
  idToken: "",
  lang: "en-US"
};

export default EB360MapComponent;